/**
 * The property walk's offline queue.
 *
 * A walk happens on the customer's property, where there is often no signal. So
 * every entry is written to IndexedDB FIRST and uploaded after. The phone is the
 * record until the server has it.
 *
 * Its own database ('rbf-walk'), not the driver's: the driver queue's schema and
 * version stay untouched, and a manager who also drives never mixes the two.
 *
 * Each write carries an id minted when it is captured and resent verbatim on every
 * retry (the server's client_write_id), so a write that reached the server but
 * whose answer was lost is applied once. Uploads go in capture order.
 *
 *   no signal        -> kept, tried again when the phone comes back online
 *   signed out (401) -> kept, "sign in to upload"
 *   server error     -> kept, retried with backoff (5 s doubling, 10 min cap)
 *   refused (4xx)    -> kept and shown with the server's reason; never retried
 *                       by itself, so a bad entry cannot loop. Retry or discard.
 *
 * Auth is the portal's httpOnly cookies through the driver api's apiFetch (one
 * refresh on 401) - never a token in browser storage.
 */
import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import { apiFetch, NetworkError } from '../driver/sync/api'
import type { WalkState } from './types'

export type WriteState = 'pending' | 'failed' | 'synced'

/** nextAttemptAt for a write the server refused: it waits for a person. */
export const WAITING_FOR_PERSON = Number.MAX_SAFE_INTEGER

export interface WalkWrite {
  id: string
  subscriberId: number
  path: string
  method: 'PATCH' | 'POST'
  body?: Record<string, unknown>
  photo?: { blob: Blob; kind: string; caption: string }
  label: string
  capturedAt: string
  createdAt: number
  state: WriteState
  attempts: number
  lastError: string
  nextAttemptAt: number
}

interface WalkDB extends DBSchema {
  writes: { key: string; value: WalkWrite; indexes: { 'by-subscriber': number } }
  snapshots: { key: number; value: { subscriberId: number; savedAt: number; state: WalkState } }
  meta: { key: string; value: { key: string; value: unknown } }
}

let dbPromise: Promise<IDBPDatabase<WalkDB>> | null = null

function db(): Promise<IDBPDatabase<WalkDB>> {
  if (!dbPromise) {
    dbPromise = openDB<WalkDB>('rbf-walk', 1, {
      upgrade(d) {
        const writes = d.createObjectStore('writes', { keyPath: 'id' })
        writes.createIndex('by-subscriber', 'subscriberId')
        d.createObjectStore('snapshots', { keyPath: 'subscriberId' })
        d.createObjectStore('meta', { keyPath: 'key' })
      },
    })
  }
  return dbPromise
}

const listeners = new Set<() => void>()

export function onWalkChange(fn: () => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

function emit(): void {
  listeners.forEach((fn) => fn())
}

export function newWriteId(): string {
  return crypto.randomUUID()
}

// ── snapshots: the last walk state the server sent, so the screen opens offline ──

export async function saveSnapshot(state: WalkState): Promise<void> {
  await (await db()).put('snapshots', { subscriberId: state.subscriber.id, savedAt: Date.now(), state })
  emit()
}

export async function loadSnapshot(subscriberId: number): Promise<{ savedAt: number; state: WalkState } | undefined> {
  return (await db()).get('snapshots', subscriberId)
}

export async function setWalkMeta(key: string, value: unknown): Promise<void> {
  await (await db()).put('meta', { key, value })
}

export async function getWalkMeta<T = unknown>(key: string): Promise<T | undefined> {
  const row = await (await db()).get('meta', key)
  return row ? (row.value as T) : undefined
}

// ── the queue ────────────────────────────────────────────────────────────────

export async function listWrites(subscriberId: number): Promise<WalkWrite[]> {
  const rows = await (await db()).getAllFromIndex('writes', 'by-subscriber', subscriberId)
  return rows.sort((a, b) => a.createdAt - b.createdAt)
}

type NewWrite = Pick<WalkWrite, 'subscriberId' | 'path' | 'method' | 'label'> & Partial<Pick<WalkWrite, 'body' | 'photo'>>

/** Save the entry on the phone, then try to upload. Resolves once it is SAVED. */
export async function enqueue(write: NewWrite): Promise<WalkWrite> {
  const rec: WalkWrite = {
    ...write,
    id: newWriteId(),
    capturedAt: new Date().toISOString(),
    createdAt: Date.now(),
    state: 'pending',
    attempts: 0,
    lastError: '',
    nextAttemptAt: 0,
  }
  await (await db()).put('writes', rec)
  emit()
  void drainWalk()
  return rec
}

export async function retryWrite(id: string): Promise<void> {
  const d = await db()
  const w = await d.get('writes', id)
  if (!w) return
  await d.put('writes', { ...w, state: 'pending', nextAttemptAt: 0 })
  emit()
  void drainWalk()
}

export async function discardWrite(id: string): Promise<void> {
  await (await db()).delete('writes', id)
  emit()
}

const backoff = (attempts: number) => Math.min(5000 * 2 ** Math.max(0, attempts - 1), 10 * 60 * 1000)

function send(w: WalkWrite): Promise<Response> {
  if (w.photo) {
    const form = new FormData()
    form.append('kind', w.photo.kind)
    form.append('caption', w.photo.caption)
    form.append('client_write_id', w.id)
    form.append('captured_at', w.capturedAt)
    form.append('image', w.photo.blob, `${w.photo.kind}.jpg`)
    return apiFetch(w.path, { method: w.method, body: form })
  }
  return apiFetch(w.path, {
    method: w.method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...(w.body || {}), client_write_id: w.id, captured_at: w.capturedAt }),
  })
}

let draining = false

/** Upload what is waiting, in capture order. Safe to call any time. */
export async function drainWalk(): Promise<void> {
  if (draining) return
  draining = true
  try {
    const d = await db()
    const waiting = (await d.getAll('writes'))
      .filter((w) => w.state !== 'synced')
      .sort((a, b) => a.createdAt - b.createdAt)
    for (const w of waiting) {
      if (w.nextAttemptAt > Date.now()) continue
      let res: Response
      try {
        res = await send(w)
      } catch (err) {
        if (err instanceof NetworkError) {
          await d.put('writes', { ...w, attempts: w.attempts + 1, lastError: 'No signal. Saved on this phone.' })
          break
        }
        throw err
      }
      const text = await res.text()
      let body: any = null
      try {
        body = text ? JSON.parse(text) : null
      } catch {
        body = null
      }
      if (res.ok) {
        await d.put('writes', { ...w, state: 'synced', lastError: '' })
        if (body && body.subscriber) {
          await d.put('snapshots', { subscriberId: body.subscriber.id, savedAt: Date.now(), state: body as WalkState })
        }
        continue
      }
      if (res.status === 401) {
        await d.put('writes', { ...w, lastError: 'Signed out. Sign in to upload; nothing is lost.' })
        break
      }
      const attempts = w.attempts + 1
      const refused = res.status >= 400 && res.status < 500 && ![408, 409, 429].includes(res.status)
      const reason = (body && (body.error || body.detail)) || `The server answered ${res.status}.`
      await d.put('writes', {
        ...w,
        state: 'failed',
        attempts,
        lastError: String(reason),
        nextAttemptAt: refused ? WAITING_FOR_PERSON : Date.now() + backoff(attempts),
      })
    }
    // Uploaded entries stay a day (so the screen can say "uploaded"), then go.
    const cutoff = Date.now() - 24 * 60 * 60 * 1000
    for (const w of await d.getAll('writes')) {
      if (w.state === 'synced' && w.createdAt < cutoff) await d.delete('writes', w.id)
    }
  } finally {
    draining = false
    emit()
  }
}
